<template>
  <v-col>
    <v-card>
      <v-row no-gutters>
        <v-card-title class="headline">
          Benchmarking
        </v-card-title>
        <v-row no-gutters class="justify-end">
          <div>
            <v-container class="d-flex align-center">
              <ProjectPageNav />
              <v-btn
                color="success"
                class="mr-4"
                :loading="isExporting"
                :disabled="isExporting || !calcResponse"
                @click="exportBenchmarkExcel"
                title="Benchmarking als Excel exportieren"
              >
                <v-icon start>mdi-file-excel</v-icon>
                Excel Export
              </v-btn>
              <!--
                `HelpBar` ist nur die Schublade; geoeffnet wird sie ueber
                `store.showSidebar(<Hilfekomponente>)`. Das fruehere
                `<HelpBar :help="BenchmarkingHelp" />` reichte eine Eigenschaft herein, die
                es an `HelpBar` gar nicht gibt — die Hilfe liess sich auf dieser Seite
                also nie oeffnen. So macht es die Seite Einsparpotential schon immer.
              -->
              <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    color="primary"
                    variant="flat"
                    class="mr-4"
                    @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(BenchmarkingHelp)"
                  >
                    <v-icon start>
                      {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                    </v-icon>
                    Hilfe
                  </v-btn>
                </template>
              </v-tooltip>
              <HelpBar />
            </v-container>
          </div>
        </v-row>
      </v-row>

      <div v-if="errorResponse">
        <v-card-text>
          <v-alert type="error" variant="outlined">
            <div>{{ errorResponse }}</div>
          </v-alert>
        </v-card-text>
      </div>

      <div v-if="warningResponse.length">
        <v-card-text>
          <v-alert type="warning" variant="outlined">
            <div v-for="warning in warningResponse" :key="warning">{{ warning }}</div>
          </v-alert>
        </v-card-text>
      </div>

      <div class="pa-4" v-if="!errorResponse">
        <v-alert v-if="!hasValidArea" type="warning" variant="outlined" class="mb-4">
          Für flächenbezogene Kennwerte bitte eine Bruttogrundfläche in den Projektdetails hinterlegen.
        </v-alert>

        <v-row no-gutters>
          <v-col cols="12" md="4" class="pr-md-2">
            <v-card variant="outlined" class="pa-4 metric-card h-100">
              <div class="subtitle-1 font-weight-bold mb-3">Euro-Kennwerte [€/m²]</div>
              <div class="metric-item">
                <span>Investition</span>
                <strong>{{ formatNumber(investPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Jährliche Einsparung</span>
                <strong>{{ formatNumber(costSavingsPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>NPV (VALERI)</span>
                <strong>{{ formatNumber(npvPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Annuität (VALERI)</span>
                <strong>{{ formatNumber(annuityPerM2) }}</strong>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="px-md-2 mt-4 mt-md-0">
            <v-card variant="outlined" class="pa-4 metric-card h-100">
              <div class="subtitle-1 font-weight-bold mb-3">Energie-Kennwerte [kWh/(m²·a)]</div>
              <div class="metric-item">
                <span>Primärenergie Referenz</span>
                <strong>{{ formatNumber(refPrimaryEnergyPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Primärenergie Geplant</span>
                <strong>{{ formatNumber(planPrimaryEnergyPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Endenergie Referenz</span>
                <strong>{{ formatNumber(refEndEnergyPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Endenergie Geplant</span>
                <strong>{{ formatNumber(planEndEnergyPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Energieeinsparung</span>
                <strong>{{ formatNumber(energySavingsPerM2) }}</strong>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="pl-md-2 mt-4 mt-md-0">
            <v-card variant="outlined" class="pa-4 metric-card h-100">
              <div class="subtitle-1 font-weight-bold mb-3">CO₂-Kennwerte [kg/(m²·a)]</div>
              <div class="metric-item">
                <span>CO₂ Referenz</span>
                <strong>{{ formatNumber(refCo2PerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>CO₂ Geplant</span>
                <strong>{{ formatNumber(planCo2PerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>CO₂ Einsparung</span>
                <strong>{{ formatNumber(co2SavingsPerM2) }}</strong>
              </div>
              <div class="metric-item">
                <span>Dynamische Amortisation [a]</span>
                <strong>{{ formatPayback(discountedPaybackYears) }}</strong>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4">
          <v-col cols="12" md="4" class="pr-md-2 mb-4 mb-md-0">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">Benchmark Euro [€/m²]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkEuroChart', 'benchmark-euro')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(euroBenchmarkChartData, 'benchmark-euro')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper">
                <standard-bar-chart
                  ref="benchmarkEuroChart"
                  :chart-data="euroBenchmarkChartData"
                  :options="euroBenchmarkChartOptions"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="px-md-2 mb-4 mb-md-0">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">Benchmark Energie [kWh/(m²·a)]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkEnergyChart', 'benchmark-energie')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(energyBenchmarkChartData, 'benchmark-energie')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper">
                <standard-bar-chart
                  ref="benchmarkEnergyChart"
                  :chart-data="energyBenchmarkChartData"
                  :options="energyBenchmarkChartOptions"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="pl-md-2">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">Benchmark CO₂ [kg/(m²·a)]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkCo2Chart', 'benchmark-co2')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(co2BenchmarkChartData, 'benchmark-co2')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper">
                <standard-bar-chart
                  ref="benchmarkCo2Chart"
                  :chart-data="co2BenchmarkChartData"
                  :options="co2BenchmarkChartOptions"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="section-title mt-8 mb-2">Benchmark pro Maßnahme</div>
        <v-row no-gutters>
          <v-col cols="12" md="4" class="pr-md-2 mb-4 mb-md-0">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">Euro pro Maßnahme [€/m²]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkPerActionEuroChart', 'benchmark-pro-massnahme-euro')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(perActionEuroChartData, 'benchmark-pro-massnahme-euro')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper" :style="{ height: perActionChartHeight + 'px' }">
                <standard-bar-chart
                  ref="benchmarkPerActionEuroChart"
                  :chart-data="perActionEuroChartData"
                  :options="perActionEuroChartOptions"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="px-md-2 mb-4 mb-md-0">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">Energie pro Maßnahme [kWh/(m²·a)]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkPerActionEnergyChart', 'benchmark-pro-massnahme-energie')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(perActionEnergyChartData, 'benchmark-pro-massnahme-energie')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper" :style="{ height: perActionChartHeight + 'px' }">
                <standard-bar-chart
                  ref="benchmarkPerActionEnergyChart"
                  :chart-data="perActionEnergyChartData"
                  :options="perActionEnergyChartOptions"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="pl-md-2">
            <v-card class="pa-4" outlined style="position: relative;">
              <div class="chart-title">CO₂ pro Maßnahme [kg/(m²·a)]</div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('benchmarkPerActionCo2Chart', 'benchmark-pro-massnahme-co2')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(perActionCo2ChartData, 'benchmark-pro-massnahme-co2')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper" :style="{ height: perActionChartHeight + 'px' }">
                <standard-bar-chart
                  ref="benchmarkPerActionCo2Chart"
                  :chart-data="perActionCo2ChartData"
                  :options="perActionCo2ChartOptions"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="actionGroupResults.length > 0">
          <div class="section-title mt-8 mb-2">Benchmark pro Maßnahmengruppe</div>
          <v-row no-gutters>
            <v-col cols="12" md="4" class="pr-md-2 mb-4 mb-md-0">
              <v-card class="pa-4" outlined style="position: relative;">
                <div class="chart-title">Euro pro Maßnahmengruppe [€/m²]</div>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      color="primary"
                      v-bind="props"
                      title="Optionen"
                      style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="downloadChartImageByRef('benchmarkPerGroupEuroChart', 'benchmark-pro-gruppe-euro')">
                      <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                      <v-list-item-title>Grafik herunterladen</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="exportChartDataExcelGeneric(perGroupEuroChartData, 'benchmark-pro-gruppe-euro')">
                      <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                      <v-list-item-title>Daten exportieren</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <div class="chart-wrapper" :style="{ height: perGroupChartHeight + 'px' }">
                  <standard-bar-chart
                    ref="benchmarkPerGroupEuroChart"
                    :chart-data="perGroupEuroChartData"
                    :options="perGroupEuroChartOptions"
                  />
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4" class="px-md-2 mb-4 mb-md-0">
              <v-card class="pa-4" outlined style="position: relative;">
                <div class="chart-title">Energie pro Maßnahmengruppe [kWh/(m²·a)]</div>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      color="primary"
                      v-bind="props"
                      title="Optionen"
                      style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="downloadChartImageByRef('benchmarkPerGroupEnergyChart', 'benchmark-pro-gruppe-energie')">
                      <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                      <v-list-item-title>Grafik herunterladen</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="exportChartDataExcelGeneric(perGroupEnergyChartData, 'benchmark-pro-gruppe-energie')">
                      <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                      <v-list-item-title>Daten exportieren</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <div class="chart-wrapper" :style="{ height: perGroupChartHeight + 'px' }">
                  <standard-bar-chart
                    ref="benchmarkPerGroupEnergyChart"
                    :chart-data="perGroupEnergyChartData"
                    :options="perGroupEnergyChartOptions"
                  />
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4" class="pl-md-2">
              <v-card class="pa-4" outlined style="position: relative;">
                <div class="chart-title">CO₂ pro Maßnahmengruppe [kg/(m²·a)]</div>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      color="primary"
                      v-bind="props"
                      title="Optionen"
                      style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="downloadChartImageByRef('benchmarkPerGroupCo2Chart', 'benchmark-pro-gruppe-co2')">
                      <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                      <v-list-item-title>Grafik herunterladen</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="exportChartDataExcelGeneric(perGroupCo2ChartData, 'benchmark-pro-gruppe-co2')">
                      <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                      <v-list-item-title>Daten exportieren</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <div class="chart-wrapper" :style="{ height: perGroupChartHeight + 'px' }">
                  <standard-bar-chart
                    ref="benchmarkPerGroupCo2Chart"
                    :chart-data="perGroupCo2ChartData"
                    :options="perGroupCo2ChartOptions"
                  />
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>
    </v-card>
  </v-col>
</template>

<script>
import StandardBarChart from "@/components/calcpreview/StandardBarChart.vue";
import HelpBar from "@/components/HelpBar.vue";
import ProjectPageNav from "@/components/ProjectPageNav.vue";
import BenchmarkingHelp from "@/components/help/BenchmarkingHelp.vue";
import { useProjectStore } from "@/stores/projectStore";
import { storeDocxModuleCharts } from "@/utils/docxAppendixCache";
import { exportChartExcel } from "@/utils/chartDataExport";
import api from "@/plugins/axios";
import { toast } from "vue3-toastify";

const END_ENERGY_IDS = [1, 2, 3, 4, 5, 6, 7];

export default {
  name: "ProjectBenchmarking",
  setup() {
    const store = useProjectStore();
    return { store };
  },
  components: {
    StandardBarChart,
    HelpBar,
    ProjectPageNav,
  },
  data() {
    return {
      calcResponse: null,
      valeriResponse: null,
      errorResponse: null,
      warningResponse: [],
      actionCalcList: [],
      consMitGens: {},
      fuels: {},
      loading: false,
      isExporting: false,
      isDocxExporting: false,
      BenchmarkingHelp,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    project() {
      return this.store.currentProject;
    },
    actionList() {
      return this.store.currentProjectActions || [];
    },
    groups() {
      const source = this.store.currentProjectActionGroups || [];
      return source.reduce((result, group) => {
        result[group.id] = group;
        return result;
      }, {});
    },
    /*
     * Ohne weiteren Filter: die Anfrage schickt nur die angehakten Maßnahmen,
     * also enthält die Antwort auch nur diese. Hier stand eine Prüfung gegen
     * `noCalc`/`noCalcGroup` — eine tote, rein lokale Liste, die immer leer
     * war und deshalb nichts entfernt hat.
     *
     * `ActionGroup.checked` taugt für die Gruppen nicht als Ersatz: der
     * Vorgabewert ist False, und `get_action_groups_calc` liefert die Gruppen
     * anhand der gewählten Maßnahmen, nicht anhand dieses Häkchens.
     */
    actionGroupResults() {
      return this.calcResponse?.result?.actionGroups || [];
    },
    allActionResults() {
      const ungrouped = this.calcResponse?.result?.actions || [];
      const grouped = this.actionGroupResults.flatMap((group) => group.actions || []);
      const seen = {};
      return [...ungrouped, ...grouped].filter((item) => {
        if (!item || seen[item.id]) {
          return false;
        }
        seen[item.id] = true;
        return true;
      });
    },
    valeriActionMap() {
      const directActions = this.valeriResponse?.result?.actions || [];
      const groupedActions = (this.valeriResponse?.result?.actionGroups || []).flatMap(
        (group) => group.actions || []
      );
      return [...directActions, ...groupedActions].reduce((result, action) => {
        if (action?.id !== undefined && action?.id !== null) {
          result[action.id] = action;
        }
        return result;
      }, {});
    },
    valeriGroupMap() {
      return (this.valeriResponse?.result?.actionGroups || []).reduce((result, group) => {
        if (group?.id !== undefined && group?.id !== null) {
          result[group.id] = group;
        }
        return result;
      }, {});
    },
    projectArea() {
      const area = Number(this.project?.area);
      return Number.isFinite(area) && area > 0 ? area : 0;
    },
    hasValidArea() {
      return this.projectArea > 0;
    },
    calcTotal() {
      return this.calcResponse?.result?.total || {};
    },
    valeriTotal() {
      return this.valeriResponse?.result?.total || {};
    },
    refEnergyBalance() {
      return this.calcTotal?.ref_energy || [];
    },
    planEnergyBalance() {
      return this.calcTotal?.plan_energy || [];
    },
    investTotal() {
      return this.asNumber(this.calcTotal?.invest);
    },
    annualSavingsTotal() {
      return this.asNumber(this.calcTotal?.savings);
    },
    npvTotal() {
      return this.asNumber(this.valeriTotal?.capitalValue);
    },
    annuityTotal() {
      return this.asNumber(this.valeriTotal?.annuity);
    },
    discountedPaybackYears() {
      const value = Number(this.valeriTotal?.discountedPayback);
      return Number.isFinite(value) ? value : null;
    },
    refEndEnergyTotal() {
      return this.sumEndEnergy(this.refEnergyBalance);
    },
    planEndEnergyTotal() {
      return this.sumEndEnergy(this.planEnergyBalance);
    },
    endEnergySavingsTotal() {
      return this.refEndEnergyTotal - this.planEndEnergyTotal;
    },
    /*
     * Aus der Rechnung, nicht hier gebildet.
     *
     * `sumPrimaryEnergy` summierte nur die Endenergieflüsse der
     * zusammengefassten Bilanz und liess damit den Strom- und Kältebedarf aus,
     * den die Nutzenergiefaktoren bewerten — auf Demo2 standen hier 109,32
     * statt 149,40 kWh/(m²·a), und die Spalte „gesamt" fiel mit „nicht
     * erneuerbar" zusammen. Dieselbe Abweichung hatten die Berichte, bis sie
     * je Maßnahme über die Bilanzmethode rechneten.
     *
     * Nachbilden lässt sich das hier nicht: die Nutzenergiefaktoren leitet
     * `costsorter.simple_ne_specs` aus der Verbrauchsbaseline ab, die der
     * Oberfläche nicht vorliegt. Also liefert sie der Server mit — wie die
     * marktbasierten CO₂-Werte.
     */
    refPrimaryEnergyTotal() {
      return this.asNumber(this.calcTotal?.ref_primary);
    },
    planPrimaryEnergyTotal() {
      return this.asNumber(this.calcTotal?.plan_primary);
    },
    primaryEnergySavingsTotal() {
      return this.refPrimaryEnergyTotal - this.planPrimaryEnergyTotal;
    },
    /* Zweite Spalte der DIN V 18599-1 Tabelle A.1: einschliesslich des
       erneuerbaren Anteils. Bei Holz stehen 1,2 gegen 0,2 — ohne diese Zahl
       sieht eine Umstellung auf Biomasse wie eine reine Einsparung aus. */
    refPrimaryEnergyGesamtTotal() {
      return this.asNumber(this.calcTotal?.ref_primary_total);
    },
    planPrimaryEnergyGesamtTotal() {
      return this.asNumber(this.calcTotal?.plan_primary_total);
    },
    energySavingsTotal() {
      return this.endEnergySavingsTotal;
    },
    /* In Gramm, wie die Rechnung sie liefert. Umgerechnet wird erst an der
       Anzeige, in `co2PerArea` — so wie ProjectCalc.vue es auch hält. */
    refCo2Total() {
      return this.asNumber(this.calcTotal?.ref_co2);
    },
    planCo2Total() {
      return this.asNumber(this.calcTotal?.plan_co2);
    },
    co2SavingsTotal() {
      const explicit = this.asNumber(this.calcTotal?.co2Savings);
      if (explicit !== 0) {
        return explicit;
      }
      return this.refCo2Total - this.planCo2Total;
    },
    /* Die marktbasierte Sicht des GHG Protocol Scope 2. Sie kommt aus der
       Rechnung, weil sie die Nutzenergie-Faktoren braucht, die sich hier nicht
       nachbilden lassen. Ohne Liefervertrag ist sie gleich der
       standortbasierten. */
    refCo2MarketTotal() {
      return this.asNumber(this.calcTotal?.ref_co2_market);
    },
    planCo2MarketTotal() {
      return this.asNumber(this.calcTotal?.plan_co2_market);
    },
    co2MarketSavingsTotal() {
      return this.refCo2MarketTotal - this.planCo2MarketTotal;
    },
    investPerM2() {
      return this.perArea(this.investTotal);
    },
    costSavingsPerM2() {
      return this.perArea(this.annualSavingsTotal);
    },
    npvPerM2() {
      return this.perArea(this.npvTotal);
    },
    annuityPerM2() {
      return this.perArea(this.annuityTotal);
    },
    refEndEnergyPerM2() {
      return this.perArea(this.refEndEnergyTotal);
    },
    planEndEnergyPerM2() {
      return this.perArea(this.planEndEnergyTotal);
    },
    energySavingsPerM2() {
      return this.perArea(this.energySavingsTotal);
    },
    refPrimaryEnergyPerM2() {
      return this.perArea(this.refPrimaryEnergyTotal);
    },
    planPrimaryEnergyPerM2() {
      return this.perArea(this.planPrimaryEnergyTotal);
    },
    primaryEnergySavingsPerM2() {
      return this.perArea(this.primaryEnergySavingsTotal);
    },
    refCo2PerM2() {
      return this.co2PerArea(this.refCo2Total);
    },
    planCo2PerM2() {
      return this.co2PerArea(this.planCo2Total);
    },
    co2SavingsPerM2() {
      return this.co2PerArea(this.co2SavingsTotal);
    },
    refPrimaryEnergyGesamtPerM2() {
      return this.perArea(this.refPrimaryEnergyGesamtTotal);
    },
    planPrimaryEnergyGesamtPerM2() {
      return this.perArea(this.planPrimaryEnergyGesamtTotal);
    },
    refCo2MarketPerM2() {
      return this.co2PerArea(this.refCo2MarketTotal);
    },
    planCo2MarketPerM2() {
      return this.co2PerArea(this.planCo2MarketTotal);
    },
    co2MarketSavingsPerM2() {
      return this.co2PerArea(this.co2MarketSavingsTotal);
    },
    euroBenchmarkChartData() {
      return {
        labels: ["Investition", "Jährliche Einsparung", "Kapitalwert (NPV)", "Annuität"],
        datasets: [
          {
            label: "Euro je m²",
            data: [
              this.investPerM2,
              this.costSavingsPerM2,
              this.npvPerM2,
              this.annuityPerM2,
            ],
            backgroundColor: [
              "rgba(21, 101, 192, 0.75)",
              "rgba(46, 125, 50, 0.75)",
              "rgba(106, 27, 154, 0.75)",
              "rgba(239, 108, 0, 0.75)",
            ],
            borderColor: ["#1565c0", "#2e7d32", "#6a1b9a", "#ef6c00"],
            borderWidth: 1,
          },
        ],
      };
    },
    euroBenchmarkChartOptions() {
      return this.baseBarOptions("Benchmark Euro [€/m²]", "€/m²");
    },
    energyBenchmarkChartData() {
      return {
        labels: [
          "Primärenergie Ref",
          "Primärenergie Plan",
          "Endenergie Ref",
          "Endenergie Plan",
          "Energieeinsparung",
        ],
        datasets: [
          {
            label: "Energie je m²",
            data: [
              this.refPrimaryEnergyPerM2,
              this.planPrimaryEnergyPerM2,
              this.refEndEnergyPerM2,
              this.planEndEnergyPerM2,
              this.energySavingsPerM2,
            ],
            backgroundColor: [
              "rgba(21, 101, 192, 0.75)",
              "rgba(2, 119, 189, 0.75)",
              "rgba(46, 125, 50, 0.75)",
              "rgba(67, 160, 71, 0.75)",
              "rgba(239, 108, 0, 0.75)",
            ],
            borderColor: ["#1565c0", "#0277bd", "#2e7d32", "#43a047", "#ef6c00"],
            borderWidth: 1,
          },
        ],
      };
    },
    energyBenchmarkChartOptions() {
      return this.baseBarOptions("Benchmark Energie [kWh/(m²·a)]", "kWh/(m²·a)");
    },
    co2BenchmarkChartData() {
      return {
        labels: ["CO₂ Referenz", "CO₂ Geplant", "CO₂ Einsparung"],
        datasets: [
          {
            label: "CO₂ je m²",
            data: [this.refCo2PerM2, this.planCo2PerM2, this.co2SavingsPerM2],
            backgroundColor: [
              "rgba(173, 20, 87, 0.75)",
              "rgba(123, 31, 162, 0.75)",
              "rgba(56, 142, 60, 0.75)",
            ],
            borderColor: ["#ad1457", "#7b1fa2", "#388e3c"],
            borderWidth: 1,
          },
        ],
      };
    },
    co2BenchmarkChartOptions() {
      return this.baseBarOptions("Benchmark CO₂ [kg/(m²·a)]", "kg/(m²·a)");
    },
    perActionLabels() {
      return this.allActionResults.map((action) => action?.name || `Maßnahme ${action?.id ?? ""}`);
    },
    perActionEuroChartData() {
      return {
        labels: this.perActionLabels,
        datasets: [
          {
            label: "Investition",
            data: this.allActionResults.map((action) => this.perArea(this.asNumber(action?.invest))),
            backgroundColor: this.toRgba(this.pickSeriesColor(0), 0.75),
            borderColor: this.pickSeriesColor(0),
            borderWidth: 1,
          },
          {
            label: "Jährliche Einsparung",
            data: this.allActionResults.map((action) =>
              this.perArea(this.asNumber(action?.savings ?? action?.energyCostSavings))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
          {
            label: "Kapitalwert (NPV)",
            data: this.allActionResults.map((action) =>
              this.perArea(this.asNumber(this.valeriActionMap[action.id]?.capitalValue))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(2), 0.75),
            borderColor: this.pickSeriesColor(2),
            borderWidth: 1,
          },
          {
            label: "Annuität",
            data: this.allActionResults.map((action) =>
              this.perArea(this.asNumber(this.valeriActionMap[action.id]?.annuity))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(3), 0.75),
            borderColor: this.pickSeriesColor(3),
            borderWidth: 1,
          },
        ],
      };
    },
    perActionEuroChartOptions() {
      return this.horizontalGroupedBarOptions("Euro pro Maßnahme [€/m²]", "€/m²");
    },
    perActionEnergyChartData() {
      return {
        labels: this.perActionLabels,
        datasets: [
          {
            label: "Endenergie Referenz",
            data: this.allActionResults.map((action) =>
              this.perArea(this.sumEndEnergy(action?.energyRef || []))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(0), 0.75),
            borderColor: this.pickSeriesColor(0),
            borderWidth: 1,
          },
          {
            label: "Endenergie Geplant",
            data: this.allActionResults.map((action) =>
              this.perArea(this.sumEndEnergy(action?.energyPlan || []))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
          {
            label: "Energieeinsparung",
            data: this.allActionResults.map((action) => {
              const reference = this.sumEndEnergy(action?.energyRef || []);
              const planned = this.sumEndEnergy(action?.energyPlan || []);
              return this.perArea(reference - planned);
            }),
            backgroundColor: this.toRgba(this.pickSeriesColor(3), 0.75),
            borderColor: this.pickSeriesColor(3),
            borderWidth: 1,
          },
        ],
      };
    },
    perActionEnergyChartOptions() {
      return this.horizontalGroupedBarOptions("Energie pro Maßnahme [kWh/(m²·a)]", "kWh/(m²·a)");
    },
    perActionCo2ChartData() {
      return {
        labels: this.perActionLabels,
        datasets: [
          {
            label: "CO₂ Referenz",
            data: this.allActionResults.map((action) => this.co2PerArea(action?.ref_co2)),
            backgroundColor: this.toRgba(this.pickSeriesColor(5), 0.75),
            borderColor: this.pickSeriesColor(5),
            borderWidth: 1,
          },
          {
            label: "CO₂ Geplant",
            data: this.allActionResults.map((action) => this.co2PerArea(action?.plan_co2)),
            backgroundColor: this.toRgba(this.pickSeriesColor(2), 0.75),
            borderColor: this.pickSeriesColor(2),
            borderWidth: 1,
          },
          {
            label: "CO₂ Einsparung",
            data: this.allActionResults.map((action) =>
              this.co2PerArea(this.co2SavingsFromResult(action))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
        ],
      };
    },
    perActionCo2ChartOptions() {
      return this.horizontalGroupedBarOptions("CO₂ pro Maßnahme [kg/(m²·a)]", "kg/(m²·a)");
    },
    perGroupLabels() {
      return this.actionGroupResults.map(
        (group) => this.groups[group.id]?.name || group?.name || `Gruppe ${group.id}`
      );
    },
    perGroupEuroChartData() {
      return {
        labels: this.perGroupLabels,
        datasets: [
          {
            label: "Investition",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.asNumber(group?.total?.invest))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(0), 0.75),
            borderColor: this.pickSeriesColor(0),
            borderWidth: 1,
          },
          {
            label: "Jährliche Einsparung",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.asNumber(group?.total?.savings ?? group?.total?.energyCostSavings))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
          {
            label: "Kapitalwert (NPV)",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.asNumber(this.valeriGroupMap[group.id]?.capitalValue))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(2), 0.75),
            borderColor: this.pickSeriesColor(2),
            borderWidth: 1,
          },
          {
            label: "Annuität",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.asNumber(this.valeriGroupMap[group.id]?.annuity))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(3), 0.75),
            borderColor: this.pickSeriesColor(3),
            borderWidth: 1,
          },
        ],
      };
    },
    perGroupEuroChartOptions() {
      return this.horizontalGroupedBarOptions("Euro pro Maßnahmengruppe [€/m²]", "€/m²");
    },
    perGroupEnergyChartData() {
      return {
        labels: this.perGroupLabels,
        datasets: [
          {
            label: "Endenergie Referenz",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.sumEndEnergy(group?.total?.ref_energy || []))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(0), 0.75),
            borderColor: this.pickSeriesColor(0),
            borderWidth: 1,
          },
          {
            label: "Endenergie Geplant",
            data: this.actionGroupResults.map((group) =>
              this.perArea(this.sumEndEnergy(group?.total?.plan_energy || []))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
          {
            label: "Energieeinsparung",
            data: this.actionGroupResults.map((group) => {
              const reference = this.sumEndEnergy(group?.total?.ref_energy || []);
              const planned = this.sumEndEnergy(group?.total?.plan_energy || []);
              return this.perArea(reference - planned);
            }),
            backgroundColor: this.toRgba(this.pickSeriesColor(3), 0.75),
            borderColor: this.pickSeriesColor(3),
            borderWidth: 1,
          },
        ],
      };
    },
    perGroupEnergyChartOptions() {
      return this.horizontalGroupedBarOptions("Energie pro Maßnahmengruppe [kWh/(m²·a)]", "kWh/(m²·a)");
    },
    perGroupCo2ChartData() {
      return {
        labels: this.perGroupLabels,
        datasets: [
          {
            label: "CO₂ Referenz",
            data: this.actionGroupResults.map((group) =>
              this.co2PerArea(group?.total?.ref_co2)
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(5), 0.75),
            borderColor: this.pickSeriesColor(5),
            borderWidth: 1,
          },
          {
            label: "CO₂ Geplant",
            data: this.actionGroupResults.map((group) =>
              this.co2PerArea(group?.total?.plan_co2)
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(2), 0.75),
            borderColor: this.pickSeriesColor(2),
            borderWidth: 1,
          },
          {
            label: "CO₂ Einsparung",
            data: this.actionGroupResults.map((group) =>
              this.co2PerArea(this.co2SavingsFromResult(group?.total || {}))
            ),
            backgroundColor: this.toRgba(this.pickSeriesColor(1), 0.75),
            borderColor: this.pickSeriesColor(1),
            borderWidth: 1,
          },
        ],
      };
    },
    perGroupCo2ChartOptions() {
      return this.horizontalGroupedBarOptions("CO₂ pro Maßnahmengruppe [kg/(m²·a)]", "kg/(m²·a)");
    },
    // Horizontal charts grow with the number of categories so every bar stays readable.
    perActionChartHeight() {
      return Math.max(340, this.allActionResults.length * 110);
    },
    perGroupChartHeight() {
      return Math.max(340, this.actionGroupResults.length * 130);
    },
  },
  watch: {
    actionList(actionList) {
      if (Array.isArray(actionList) && actionList.length > 0) {
        this.recalculate();
      }
    },
    project: {
      handler(project) {
        if (project) {
          this.store.setTitle(project.name);
        }
      },
      immediate: true,
    },
  },
  beforeMount() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loading = true;
      this.fetch()
        .then(() => Promise.all([this.fetchEnergyNames(), this.recalculate()]))
        .finally(() => {
          this.loading = false;
        });
    },
    fetch() {
      return this.store.fetchCurrentProject(this.projectId);
    },
    fetchEnergyNames() {
      return api
        .get("/projects/fuels/")
        .then((response) => {
          for (const fuel of response.data || []) {
            this.fuels[Number(fuel.id)] = {
              id: Number(fuel.id),
              name: fuel.name,
              primaryEnergyFactor: Number(fuel.primary_energy_factor) || 0,
            };
          }
        })
        .catch(() => {
          toast.warning("Energieträger konnten nicht geladen werden.");
        });
    },
    recalculate() {
      this.calcActionsCalcList();
      this.consMitGens = this.calcConsumerMitGenList();
      return Promise.all([this.calcActions(), this.calcValeri()]);
    },
    calcActionsCalcList() {
      /*
       * `forCalc` ist die Auswahl, die das Häkchen „Maßnahme berechnen" auf
       * der Maßnahmenseite setzt; sie kommt vom Server (`Action.checked`).
       *
       * Vorher wurde hier gegen `noCalc` gefiltert — eine ältere, rein lokale
       * Liste, die niemand mehr füllt. Der Filter lief also immer ins Leere und
       * diese Ansicht rechnete **alle** Maßnahmen, auch die abgewählten. Das
       * Einsparpotential war die einzige Ansicht, die es richtig gemacht hat,
       * weil es `forCalc` verwendet.
       */
      const checked = new Set(this.store.forCalc);
      this.actionCalcList = this.actionList
        .map((item) => item.id)
        .filter((id) => checked.has(id));
    },
    calcConsumerMitGenList() {
      const actionsForCalc = this.actionList.filter((item) => this.actionCalcList.includes(item.id));
      const result = {};

      actionsForCalc.forEach((item) => {
        if (Array.isArray(item.generator_type) && item.generator_type.length > 0) {
          result[item.id] = (result[item.id] || []).concat(item.generator_type);
        }
      });
      return result;
    },
    calcActions() {
      return api
        .post(`/projects/${this.projectId}/calc/`, {
          params: { actions: this.actionCalcList, ConsMitGen_ids: this.consMitGens },
        })
        .then((response) => {
          this.calcResponse = response.data;
          this.warningResponse = response.data?.result?.warnings || [];
          this.errorResponse = null;
        })
        .catch((error) => {
          const detail = error.response?.data;
          this.errorResponse = Array.isArray(detail)
            ? detail.join(" ")
            : detail?.detail || "Die Benchmark-Berechnung konnte nicht durchgeführt werden.";
        });
    },
    calcValeri() {
      return api
        .post(`/projects/${this.projectId}/calc/valeri/`, {
          params: { actions: this.actionCalcList, ConsMitGen_ids: this.consMitGens },
        })
        .then((response) => {
          this.valeriResponse = response.data;
        })
        .catch(() => {
          toast.warning("VALERI-Daten konnten nicht geladen werden.");
        });
    },
    asNumber(value) {
      const normalized = Number(value);
      return Number.isFinite(normalized) ? normalized : 0;
    },
    co2SavingsFromResult(item) {
      const explicit = this.asNumber(item?.co2Savings);
      if (explicit !== 0) {
        return explicit;
      }
      return this.asNumber(item?.ref_co2) - this.asNumber(item?.plan_co2);
    },
    perArea(value) {
      if (!this.hasValidArea) {
        return 0;
      }
      return this.asNumber(value) / this.projectArea;
    },
    /*
     * Gramm in kg/(m²·a) — die eine Stelle, an der CO₂ umgerechnet wird.
     *
     * `Balance.co2_sum` rechnet in Gramm, weil die Faktoren in g/kWh stehen,
     * und `to_dict` gibt den Wert unverändert weiter; ProjectCalc.vue teilt
     * ebenso erst an der Anzeige. Hier war die Division nirgends: sämtliche
     * CO₂-Kennwerte und -Diagramme sind mit kg beschriftet und trugen das
     * Tausendfache — auf dem Bildschirm wie in den Excel-Blättern „KPI_m2",
     * „Gesamt_CO2", „Maßnahme_CO2" und „Gruppe_CO2" (EB-15).
     */
    co2PerArea(grammes) {
      return this.perArea(this.asNumber(grammes) / 1000);
    },
    sumEndEnergy(balanceList) {
      return (balanceList || []).reduce((sum, flow) => {
        const id = Number(flow.id);
        const quantity = Number(flow.quantity) || 0;
        if (!END_ENERGY_IDS.includes(id) || quantity >= 0) {
          return sum;
        }
        return sum + Math.abs(quantity);
      }, 0);
    },
    baseBarOptions(title, yLabel) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: true,
            text: title,
            font: {
              size: 16,
            },
          },
          datalabels: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label(context) {
                return new Intl.NumberFormat("de-DE", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(Number(context.parsed.y) || 0);
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              callback(value) {
                // category axis: show the label (not the numeric index)
                return this.getLabelForValue(value);
              },
            },
          },
          y: {
            ticks: {
              callback(value) {
                return new Intl.NumberFormat("de-DE", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(value);
              },
            },
            title: {
              display: true,
              text: yLabel,
            },
          },
        },
      };
    },
    groupedBarOptions(title, yLabel) {
      const options = this.baseBarOptions(title, yLabel);
      return {
        ...options,
        plugins: {
          ...(options.plugins || {}),
          legend: {
            ...(options.plugins?.legend || {}),
            display: true,
          },
        },
      };
    },
    truncateLabel(name, max = 24) {
      const str = String(name ?? "");
      return str.length > max ? `${str.slice(0, max - 1)}…` : str;
    },
    // Horizontal grouped bars: long category names (Maßnahme/Gruppe) sit on the
    // y-axis where they are readable; values run along the x-axis.
    horizontalGroupedBarOptions(title, valueLabel) {
      const truncate = this.truncateLabel;
      const formatValue = (value) =>
        new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(value) || 0);
      return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: {
          legend: {
            display: true,
          },
          title: {
            display: true,
            text: title,
            font: {
              size: 16,
            },
          },
          datalabels: {
            display: false,
          },
          tooltip: {
            callbacks: {
              // full (untruncated) category name as the tooltip heading
              title(items) {
                return items?.[0]?.label ?? "";
              },
              label(context) {
                const seriesLabel = context.dataset?.label ? `${context.dataset.label}: ` : "";
                return seriesLabel + formatValue(context.parsed.x);
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              callback(value) {
                return formatValue(value);
              },
            },
            title: {
              display: true,
              text: valueLabel,
            },
          },
          y: {
            ticks: {
              autoSkip: false,
              callback(value) {
                // category axis: show the (truncated) name instead of the index
                return truncate(this.getLabelForValue(value));
              },
            },
          },
        },
      };
    },
    pickSeriesColor(index) {
      const palette = [
        "#1565c0",
        "#2e7d32",
        "#6a1b9a",
        "#ef6c00",
        "#00838f",
        "#ad1457",
        "#5d4037",
        "#424242",
      ];
      return palette[index % palette.length];
    },
    toRgba(hexColor, alpha) {
      const sanitized = String(hexColor || "").replace("#", "");
      if (sanitized.length !== 6) {
        return `rgba(66,66,66,${alpha})`;
      }
      const red = Number.parseInt(sanitized.substring(0, 2), 16);
      const green = Number.parseInt(sanitized.substring(2, 4), 16);
      const blue = Number.parseInt(sanitized.substring(4, 6), 16);
      return `rgba(${red},${green},${blue},${alpha})`;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(Number(value) || 0);
    },
    formatPayback(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(Number(value));
    },
    getChartByRef(refName) {
      const chartComponent = this.$refs[refName];
      if (!chartComponent) {
        return null;
      }
      if (Array.isArray(chartComponent)) {
        const instance = chartComponent[0];
        return instance?.chart || (instance?.$data && instance.$data._chart) || instance?._chart || null;
      }
      return chartComponent?.chart || (chartComponent?.$data && chartComponent.$data._chart) || chartComponent?._chart || null;
    },
    downloadChartImageByRef(refName, filePrefix) {
      const chart = this.getChartByRef(refName);
      if (!chart) {
        toast.warning("Grafik ist noch nicht bereit. Bitte erneut versuchen.");
        return;
      }

      try {
        const link = document.createElement("a");
        link.download = `${filePrefix}-projekt-${this.projectId}.png`;

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              toast.error("Grafik konnte nicht erstellt werden.");
              return;
            }
            const url = URL.createObjectURL(blob);
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, "image/png");
          return;
        }

        const dataUrl = chart.toBase64Image();
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch {
        toast.error("Grafik konnte nicht heruntergeladen werden.");
      }
    },
    exportChartDataExcelGeneric(chartData, filePrefix) {
      return exportChartExcel(this.projectId, chartData, filePrefix);
    },
    chartToBase64(chart) {
      return new Promise((resolve, reject) => {
        if (!chart) {
          resolve(null);
          return;
        }

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }, "image/png");
          return;
        }

        if (typeof chart.toBase64Image === "function") {
          resolve(chart.toBase64Image());
          return;
        }

        resolve(null);
      });
    },
    async collectBenchmarkChartsForDocx() {
      const descriptors = [
        { id: "benchmarkEuroChart", title: "Benchmark Euro [EUR/m2]", module: "Benchmarking" },
        { id: "benchmarkEnergyChart", title: "Benchmark Energie [kWh/(m2*a)]", module: "Benchmarking" },
        { id: "benchmarkCo2Chart", title: "Benchmark CO₂ [kg/(m2*a)]", module: "Benchmarking" },
        { id: "benchmarkPerActionEuroChart", title: "Euro pro Maßnahme [EUR/m2]", module: "Benchmarking" },
        { id: "benchmarkPerActionEnergyChart", title: "Energie pro Maßnahme [kWh/(m2*a)]", module: "Benchmarking" },
        { id: "benchmarkPerActionCo2Chart", title: "CO₂ pro Maßnahme [kg/(m2*a)]", module: "Benchmarking" },
        { id: "benchmarkPerGroupEuroChart", title: "Euro pro Maßnahmengruppe [EUR/m2]", module: "Benchmarking", optional: true },
        { id: "benchmarkPerGroupEnergyChart", title: "Energie pro Maßnahmengruppe [kWh/(m2*a)]", module: "Benchmarking", optional: true },
        { id: "benchmarkPerGroupCo2Chart", title: "CO₂ pro Maßnahmengruppe [kg/(m2*a)]", module: "Benchmarking", optional: true },
      ];

      const charts = [];
      for (const descriptor of descriptors) {
        const chart = this.getChartByRef(descriptor.id);
        if (!chart && descriptor.optional) {
          continue;
        }
        const imageBase64 = await this.chartToBase64(chart);
        charts.push({
          id: descriptor.id,
          title: descriptor.title,
          module: descriptor.module,
          imageBase64,
        });
      }

      return charts;
    },
    async exportDocxWithAppendix() {
      this.isDocxExporting = true;
      try {
        await this.$nextTick();
        await new Promise((resolve) => window.setTimeout(resolve, 250));

        const charts = await this.collectBenchmarkChartsForDocx();
        storeDocxModuleCharts(this.projectId, "Benchmarking", charts);
        const response = await api.post(
          `/projects/${this.projectId}/docx_extended/`,
          {
            charts,
            meta: {
              source: "benchmarking",
              schemaVersion: "1.0",
              exportedAt: new Date().toISOString(),
            },
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Einsparpotentialbericht_inkl_Anhang_${this.projectId}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("DOCX Export konnte nicht erstellt werden.");
      } finally {
        this.isDocxExporting = false;
      }
    },
    async exportBenchmarkExcel() {
      this.isExporting = true;
      try {
        const response = await api.post(
          `/projects/${this.projectId}/benchmark_excel/`,
          {
            kpis: {
              investPerM2: this.investPerM2,
              costSavingsPerM2: this.costSavingsPerM2,
              npvPerM2: this.npvPerM2,
              annuityPerM2: this.annuityPerM2,
              refPrimaryEnergyPerM2: this.refPrimaryEnergyPerM2,
              planPrimaryEnergyPerM2: this.planPrimaryEnergyPerM2,
              refPrimaryEnergyGesamtPerM2: this.refPrimaryEnergyGesamtPerM2,
              planPrimaryEnergyGesamtPerM2: this.planPrimaryEnergyGesamtPerM2,
              refEndEnergyPerM2: this.refEndEnergyPerM2,
              planEndEnergyPerM2: this.planEndEnergyPerM2,
              energySavingsPerM2: this.energySavingsPerM2,
              refCo2PerM2: this.refCo2PerM2,
              planCo2PerM2: this.planCo2PerM2,
              co2SavingsPerM2: this.co2SavingsPerM2,
              refCo2MarketPerM2: this.refCo2MarketPerM2,
              planCo2MarketPerM2: this.planCo2MarketPerM2,
              co2MarketSavingsPerM2: this.co2MarketSavingsPerM2,
              discountedPaybackYears: this.discountedPaybackYears,
            },
            charts: {
              gesamtEuro: this.euroBenchmarkChartData,
              gesamtEnergie: this.energyBenchmarkChartData,
              gesamtCo2: this.co2BenchmarkChartData,
              massnahmeEuro: this.perActionEuroChartData,
              massnahmeEnergie: this.perActionEnergyChartData,
              massnahmeCo2: this.perActionCo2ChartData,
              gruppeEuro: this.perGroupEuroChartData,
              gruppeEnergie: this.perGroupEnergyChartData,
              gruppeCo2: this.perGroupCo2ChartData,
            },
            hasValidArea: this.hasValidArea,
            projectArea: this.projectArea,
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Benchmarking-Projekt-${this.projectId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("Benchmarking konnte nicht exportiert werden.");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
.metric-card {
  min-height: 220px;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.metric-item:last-child {
  border-bottom: 0;
}

.chart-wrapper {
  position: relative;
  min-height: 340px;
}

.chart-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
}
</style>

